import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PhoneCall, MessageCircle, FileText, ArrowUpRight, Star, MapPin, ShieldCheck, Timer, BadgeCheck, Home } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";
import AssessmentForm from "../components/AssessmentForm";
import { BLOG_POSTS } from "../data/blog";
import { IMAGES } from "../data/content";
import { ACTS, DEFAULT_WA, SHOWCASE_URL, SPORT_PARAMS, sportKeyForSlug, waLink } from "../data/showcase";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/contact";
import { responsive } from "../lib/img";

const CHIPS = [["pickleball", "Pickleball"], ["netball", "Netball"], ["basketball", "Basketball"], ["tennis", "Tennis"], ["school", "Schools"], ["sda", "Accessible & SDA"]];
const TRUST = [
  { Icon: BadgeCheck, t: "Free site assessment" },
  { Icon: Timer, t: "Back in play in 3–5 days" },
  { Icon: ShieldCheck, t: "Cm3 prequalified crew" },
  { Icon: Home, t: "Merrylands HQ · Sydney-wide" },
];

const MobileAct = ({ post, i }) => {
  const act = ACTS[post.slug] || {};
  return (
    <article id={sportKeyForSlug(post.slug)} data-testid={`mobile-act-${post.slug}`}
             className={`rounded-lg border p-5 ${i === 0 ? "border-gold/40 bg-obsidian-800" : "border-white/10 bg-obsidian-800/50"}`}>
      <div className="flex items-start gap-4">
        <span className="act-number font-heading font-black text-4xl leading-none">{String(i + 1).padStart(2, "0")}</span>
        <div className="min-w-0 flex-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{act.sport || post.category}</span>
          <h2 className="mt-1 font-heading font-black text-2xl leading-[0.95] tracking-tighter">{act.headline || post.title}</h2>
        </div>
      </div>
      <Link to={`/blog-articles/${post.slug}`} className="relative mt-4 block aspect-[16/9] overflow-hidden rounded-md border border-white/10">
        <img {...responsive(IMAGES[post.hero], "100vw", [480, 800])} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
      </Link>
      <p className="mt-4 text-sm text-zinc-300">{act.pitch || post.dek}</p>
      <ul className="mt-3 space-y-1.5">
        {(act.points || post.keyTakeaways).slice(0, 3).map((pt) => (
          <li key={pt} className="flex gap-2 text-sm text-zinc-200"><Star size={13} className="mt-0.5 shrink-0 text-gold" />{pt}</li>
        ))}
      </ul>
      <div className="mt-5 grid grid-cols-2 gap-2">
        <Link to="/quote" state={{ sport: act.sport || post.category, source: post.slug }} data-testid={`mobile-act-quote-${post.slug}`}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-sm bg-gold font-heading font-black text-obsidian shadow-gold">
          <FileText size={16} /> Get a Quote
        </Link>
        <a href={waLink(act.whatsapp || DEFAULT_WA)} target="_blank" rel="noopener noreferrer" data-testid={`mobile-act-whatsapp-${post.slug}`}
           className="inline-flex h-12 items-center justify-center gap-2 rounded-sm border border-neon/50 font-bold text-neon">
          <MessageCircle size={16} /> WhatsApp
        </a>
      </div>
      <Link to={`/blog-articles/${post.slug}`} data-testid={`mobile-act-link-${post.slug}`}
            className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-gold">
        Read the full guide <ArrowUpRight size={12} />
      </Link>
    </article>
  );
};

export default function ShowcaseMobile() {
  const [params] = useSearchParams();
  const key = (params.get("sport") || "").toLowerCase();
  const variant = SPORT_PARAMS[key] || null;
  const pinnedAct = variant ? ACTS[variant.slug] : null;
  const [stats, setStats] = useState({});
  useEffect(() => {
    api.get("/blog/top", { params: { days: 30 } })
      .then((r) => setStats(Object.fromEntries(r.data.map((s) => [s.slug, s]))))
      .catch(() => {});
  }, []);
  const lineup = useMemo(() => {
    const sorted = [...BLOG_POSTS].sort((a, b) => (stats[b.slug]?.score || 0) - (stats[a.slug]?.score || 0));
    return variant ? [...sorted.filter((p) => p.slug === variant.slug), ...sorted.filter((p) => p.slug !== variant.slug)] : sorted;
  }, [stats, variant]);
  const wa = waLink(pinnedAct?.whatsapp || DEFAULT_WA);

  return (
    <div className="noise pb-24" data-testid="mobile-landing" data-sport={variant ? key : undefined}>
      <Seo title={variant ? `${variant.seo} | Call 0466 495 495` : "Court Resurfacing Sydney | Call or WhatsApp Speedy Resurfacing"}
           description="Tap to call or WhatsApp Speedy Resurfacing. Non-slip acrylic court resurfacing, crack repair and line marking for basketball, netball, tennis and school courts across Sydney. Free site assessment."
           path={SHOWCASE_URL} />

      <section className="mx-auto max-w-xl px-5 pb-8 pt-24" data-testid="mobile-hero">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold">Sydney · Merrylands HQ · Cm3 prequalified</p>
        <h1 className="mt-4 font-heading font-black text-4xl leading-[0.9] tracking-tighter sm:text-5xl" data-testid="mobile-title">
          {variant ? variant.h1 : "COURT RESURFACING"} <span className="gold-text">SYDNEY.</span>
        </h1>
        <p className="mt-4 text-base text-zinc-300">
          {pinnedAct?.pitch || "Non-slip acrylic resurfacing, crack repair and precision line marking for basketball, netball, tennis and school courts across Greater Sydney. Tap to call, WhatsApp, or send three details for an itemised quote."}
        </p>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <a href={PHONE_TEL} data-testid="mobile-call"
             className="inline-flex h-14 items-center justify-center gap-2 rounded-sm bg-gold font-heading font-black text-obsidian shadow-gold">
            <PhoneCall size={18} /> Call {PHONE_DISPLAY}
          </a>
          <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="mobile-whatsapp"
             className="inline-flex h-14 items-center justify-center gap-2 rounded-sm border border-neon/60 font-bold text-neon">
            <MessageCircle size={18} /> WhatsApp us
          </a>
        </div>
        <ul className="mt-6 grid grid-cols-2 gap-2">
          {TRUST.map(({ Icon, t }) => (
            <li key={t} className="flex items-center gap-2 rounded-sm border border-white/10 px-3 py-2 text-xs text-zinc-200"><Icon size={14} className="shrink-0 text-gold" />{t}</li>
          ))}
        </ul>
      </section>

      <nav className="mx-auto max-w-xl overflow-x-auto px-5 pb-6" aria-label="Court types" data-testid="mobile-sport-chips">
        <div className="flex w-max gap-2">
          {CHIPS.map(([k, label]) => (
            <Link key={k} to={`?sport=${k}`} replace data-testid={`mobile-chip-${k}`}
                  className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-bold transition-colors ${(SPORT_PARAMS[key] === SPORT_PARAMS[k]) ? "border-gold bg-gold text-obsidian" : "border-white/15 text-zinc-200 hover:border-gold"}`}>
              {label}
            </Link>
          ))}
        </div>
      </nav>

      <section className="mx-auto max-w-xl px-5 pb-10" data-testid="mobile-enquire">
        <AssessmentForm testid="mobile-form" source={variant ? variant.slug : "mobile-ads"} />
      </section>

      <section className="mx-auto max-w-xl space-y-4 px-5 pb-12" data-testid="mobile-lineup">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Ranked by what Sydney is reading</p>
        {lineup.map((p, i) => <MobileAct key={p.slug} post={p} i={i} />)}
      </section>

      <section className="mx-auto max-w-xl px-5 pb-10 text-sm text-zinc-400" data-testid="mobile-geo">
        <p className="inline-flex items-center gap-2"><MapPin size={14} className="text-gold" /> 545 Merrylands Road, Merrylands NSW — servicing Parramatta, Penrith, Liverpool, Blacktown, the North Shore, the Sutherland Shire, Wollongong, Newcastle and Greater Sydney.</p>
      </section>
    </div>
  );
}
